import { cn } from '@commenda/utils';

import { Sparkline } from '../charts/Sparkline';
import { ColumnHeaderHelper } from '../helpers/ColumnHeaderHelper';
import { MonthKeyHelper } from '../helpers/MonthKeyHelper';
import type { ColumnHeaderId, IComponentFacts } from '../types/report';
import { DefinitionLabel } from './DefinitionLabel';
import { moneyFormatter } from './charts/chartFormatters';

interface IColumnHeaderProps {
  column: ColumnHeaderId;
  facts: IComponentFacts;
}

export const ColumnHeader = ({ column, facts }: IColumnHeaderProps) => {
  const model = ColumnHeaderHelper.model(column, facts);
  const first = model.sparkline[0];
  const last = model.sparkline[model.sparkline.length - 1];

  return (
    <div className="flex h-full flex-col gap-lg">
      <div className="flex flex-col gap-xs">
        <p className="txt-small-regular text-text-secondary">{model.label}</p>
        <p className="text-2xl font-semibold tracking-tight tabular-nums text-text-primary">
          {model.value}
        </p>
        <p
          className={cn(
            'txt-extra-small-regular tabular-nums',
            model.change.favourable === null && 'text-text-tertiary',
            model.change.favourable === true && 'text-text-success',
            model.change.favourable === false && 'text-text-error'
          )}
        >
          {model.change.text}
        </p>
      </div>
      <dl className="flex flex-col gap-sm">
        {model.rows.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-md txt-small-regular"
          >
            <dt>
              <DefinitionLabel label={row.label} definition={row.definition} />
            </dt>
            <dd className="tabular-nums text-text-primary">{row.value}</dd>
          </div>
        ))}
      </dl>
      <Sparkline
        className="mt-auto"
        data={model.sparkline.map((p) => ({
          label: MonthKeyHelper.shortLabel(p.month),
          value: p.value,
        }))}
        valueFormatter={moneyFormatter(facts.currency)}
        startLabel={model.sparklineStart}
        endLabel={model.sparklineEnd}
        startCaption={first ? MonthKeyHelper.label(first.month) : undefined}
        endCaption={
          last ? `Current: ${MonthKeyHelper.label(last.month)}` : undefined
        }
      />
    </div>
  );
};
